class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');

    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    /* a11y-p2a-2026-09-04: listbox keyboard support. Before this, the theme shipped no key
       handling at all for the suggestion list: arrow keys did nothing, nothing ever set
       aria-selected, and the input never announced that a popup was open. Tab through the
       result links is unchanged, this only adds the combobox keys on top of it. */
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  /* ---- a11y-p2a-2026-09-04: combobox state helpers ---------------------------------- */

  get options() {
    if (!this.predictiveSearchResults) return [];
    return Array.prototype.slice.call(
      this.predictiveSearchResults.querySelectorAll('[role="option"]')
    );
  }

  get activeOption() {
    return this.options.filter((o) => o.getAttribute('aria-selected') === 'true')[0] || null;
  }

  setActiveOption(el) {
    this.options.forEach((o) => o.setAttribute('aria-selected', o === el ? 'true' : 'false'));
    if (el && el.id) {
      this.input.setAttribute('aria-activedescendant', el.id);
    } else {
      this.input.removeAttribute('aria-activedescendant');
    }
    if (el && typeof el.scrollIntoView === 'function') {
      try { el.scrollIntoView({ block: 'nearest' }); } catch (e) { /* older engines */ }
    }
  }

  clearActiveOption() {
    this.setActiveOption(null);
  }

  moveActiveOption(step) {
    const opts = this.options;
    if (!opts.length) return;
    const current = opts.indexOf(this.activeOption);
    const next = current === -1
      ? (step > 0 ? 0 : opts.length - 1)
      : (current + step + opts.length) % opts.length;
    this.setActiveOption(opts[next]);
  }

  get isOpen() {
    return !!this.predictiveSearchResults &&
      this.predictiveSearchResults.style.display === 'block';
  }

  onKeyDown(event) {
    if (!this.isOpen) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActiveOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveOption(-1);
        break;
      case 'Home':
        if (this.activeOption) { event.preventDefault(); this.setActiveOption(this.options[0]); }
        break;
      case 'End':
        if (this.activeOption) {
          event.preventDefault();
          this.setActiveOption(this.options[this.options.length - 1]);
        }
        break;
      case 'Enter': {
        // With no active option the form submits to the full search page, exactly as before.
        const active = this.activeOption;
        if (active) {
          event.preventDefault();
          active.click();
        }
        break;
      }
      case 'Escape':
        // Only drop the active option. The header script owns closing the search drawer,
        // so the event is deliberately not cancelled and not stopped.
        this.clearActiveOption();
        break;
      default:
        break;
    }
  }

  /* ---------------------------------------------------------------------------------- */

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.clearActiveOption();
    if (this.input) this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.clearActiveOption();
    if (this.input) this.input.setAttribute('aria-expanded', 'false');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
